import CustomButton from '@/components/atoms/CustomButton';
import LogoSpinner from '@/components/atoms/LogoSpinner';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import queryKeys from '@/constants/queryKeys';
import { apiPaths } from '@/services/api/paths';
import { getMetric } from '@/services/api/requests/metrics';
import colors from '@/theme/colors';
import { Badge, Box, HStack, SimpleGrid, Text, Tooltip, VStack } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { Info } from 'lucide-react';

// ---- Shapes (mirror the backend monitoring services) ----
type IdentitySnapshot = {
  internalDbReads: number;
  auth0Reads: number;
  lazySyncTriggered: number;
  syncFailures: number;
  missingInternalUser: number;
  roleSyncSuccesses: number;
  roleSyncFailures: number;
  unifiedIdentityReads: number;
  timestamp: string;
};
type IdentityHealth = { healthy: boolean; warnings: string[] };
type VideoSnapshot = {
  uploadsStarted: number;
  uploadsReady: number;
  uploadsErrored: number;
  deletionsQueued: number;
  deletionsSucceeded: number;
  deletionsFailed: number;
  webhookSignatureFailures: number;
  deleteRetryExhausted: number;
  timestamp: string;
};
type Severity = 'info' | 'warning' | 'error' | 'critical';
type Alert = { severity: Severity; message: string; metric: string; value: number; threshold: number; timestamp: string };
type HealthReport = {
  status: 'healthy' | 'degraded' | 'critical';
  alerts: Alert[];
  summary: { total: number; bySeverity: Partial<Record<Severity, number>> };
};

// ---- Metric metadata: label + plain-English meaning + whether a non-zero value is bad ----
type Meta<K extends string> = { key: K; label: string; hint: string; bad?: boolean };

const IDENTITY_METRICS: Meta<keyof IdentitySnapshot>[] = [
  { key: 'internalDbReads', label: 'Internal DB reads', hint: 'User lookups served straight from our database (the fast path).' },
  { key: 'auth0Reads', label: 'Auth0 reads', hint: 'Lookups that fell through to Auth0 — slower; should stay low.' },
  { key: 'unifiedIdentityReads', label: 'Unified identity reads', hint: 'Reads through the combined identity layer.' },
  { key: 'lazySyncTriggered', label: 'Lazy syncs', hint: 'On-demand syncs pulling a user from Auth0 into our DB.' },
  { key: 'missingInternalUser', label: 'Missing internal users', hint: 'Lookups where the user was not yet in our DB.', bad: true },
  { key: 'syncFailures', label: 'Sync failures', hint: 'Failed attempts to sync a user from Auth0.', bad: true },
  { key: 'roleSyncSuccesses', label: 'Role syncs OK', hint: 'Successful role synchronizations.' },
  { key: 'roleSyncFailures', label: 'Role sync failures', hint: 'Failed role synchronizations.', bad: true },
];

const VIDEO_METRICS: Meta<keyof VideoSnapshot>[] = [
  { key: 'uploadsStarted', label: 'Uploads started', hint: 'Mux direct uploads initiated by creators/admins.' },
  { key: 'uploadsReady', label: 'Uploads ready', hint: 'Uploads that finished processing and are playable.' },
  { key: 'uploadsErrored', label: 'Uploads errored', hint: 'Uploads that failed during processing.', bad: true },
  { key: 'deletionsQueued', label: 'Deletions queued', hint: 'Asset deletions queued for async processing.' },
  { key: 'deletionsSucceeded', label: 'Deletions done', hint: 'Asset deletions that completed successfully.' },
  { key: 'deletionsFailed', label: 'Deletions failed', hint: 'Asset deletions that failed.', bad: true },
  { key: 'webhookSignatureFailures', label: 'Webhook sig. failures', hint: 'Mux webhooks with an invalid signature (tampering/misconfig).', bad: true },
  { key: 'deleteRetryExhausted', label: 'Delete retries exhausted', hint: 'Deletions that gave up after all retries.', bad: true },
];

const STATUS_COLOR: Record<HealthReport['status'], string> = {
  healthy: colors.extra.teal,
  degraded: colors.extra.orange,
  critical: colors.extra.red,
};
const SEVERITY_COLOR: Record<Severity, string> = {
  info: colors.extra.waterBlue,
  warning: colors.extra.orange,
  error: colors.extra.red,
  critical: colors.extra.darkRed ?? colors.extra.red,
};
const SEVERITY_ORDER: Severity[] = ['critical', 'error', 'warning', 'info'];

const useMetric = <T,>(key: string, path: string) =>
  useQuery({ queryKey: [queryKeys.metrics, key], queryFn: () => getMetric<T>(path) });

const MetricTile = ({ label, value, hint, bad }: { label: string; value?: number; hint: string; bad?: boolean }) => {
  const danger = Boolean(bad) && (value ?? 0) > 0;
  return (
    <VStack
      align="flex-start"
      gap="4px"
      padding="16px 18px"
      backgroundColor="white"
      borderRadius="14px"
      border="1px solid"
      borderColor={danger ? 'extra.red' : 'background.separatorBlue'}
      boxShadow="0px 2px 10px rgba(14, 0, 98, 0.05)"
    >
      <HStack gap="5px" color="text.mediumGray">
        <Text fontSize="11px" fontWeight={700} textTransform="uppercase" letterSpacing="0.4px">
          {label}
        </Text>
        <Tooltip label={hint} placement="top" hasArrow>
          <Box as="span" lineHeight="0" cursor="help">
            <Info size={12} />
          </Box>
        </Tooltip>
      </HStack>
      <Text fontSize="28px" fontWeight={800} color={danger ? 'extra.red' : 'text.darkBlue'}>
        {value ?? '—'}
      </Text>
      <Text fontSize="12px" color="text.mediumGray" lineHeight="16px">
        {hint}
      </Text>
    </VStack>
  );
};

const SectionCard = ({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) => (
  <VStack
    background="white"
    borderRadius="20px"
    boxShadow="0px 4px 20px rgba(0, 0, 0, 0.08)"
    padding="28px"
    alignItems="stretch"
    gap="18px"
  >
    <VStack alignItems="flex-start" gap="2px">
      <Text variant="urbanistSemiBold" color="text.darkBlue" fontSize="20px">
        {title}
      </Text>
      {subtitle && (
        <Text color="text.mediumGray" fontSize="13px">
          {subtitle}
        </Text>
      )}
    </VStack>
    {children}
  </VStack>
);

const MetricsPage = () => {
  const queryClient = useQueryClient();

  const health = useMetric<HealthReport>('health', apiPaths.metrics.health);
  const identity = useMetric<IdentitySnapshot>('identitySnapshot', apiPaths.metrics.identitySnapshot);
  const identityHealth = useMetric<IdentityHealth>('identityHealth', apiPaths.metrics.identityHealth);
  const video = useMetric<VideoSnapshot>('videoSnapshot', apiPaths.metrics.videoPipelineSnapshot);

  const status = health.data?.status;
  const alerts = health.data?.alerts ?? [];
  const bySeverity = health.data?.summary?.bySeverity ?? {};
  const updatedAt = identity.data?.timestamp;

  return (
    <SidebarItemViewTemplate title="Metrics">
      <VStack padding="40px" width="100%" alignItems="stretch" gap="24px">
        <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="12px">
          <VStack alignItems="flex-start" gap="4px">
            <Text variant="urbanistSemiBold" color="text.darkBlue" fontSize="26px">
              System Metrics
            </Text>
            <Text color="text.mediumGray">
              {updatedAt ? `Snapshot taken ${format(new Date(updatedAt), 'MMM d, h:mm:ss a')}` : 'Live monitoring snapshots.'}
            </Text>
          </VStack>
          <CustomButton
            label="Refresh"
            backgroundColor="primary.500"
            labelColor="extra.white"
            width="140px"
            onClick={() => queryClient.invalidateQueries({ queryKey: [queryKeys.metrics] })}
          />
        </HStack>

        {/* Health overview + active alerts */}
        <SectionCard title="System Health" subtitle="Overall status, derived from the metric thresholds below.">
          {health.isLoading ? (
            <HStack justifyContent="center" paddingY="20px">
              <LogoSpinner width="40px" />
            </HStack>
          ) : health.isError || !health.data ? (
            <Text color="extra.red" fontSize="14px">
              Couldn’t load the health report.
            </Text>
          ) : (
            <VStack alignItems="stretch" gap="16px">
              <HStack gap="12px" flexWrap="wrap">
                <Badge
                  fontSize="13px"
                  paddingX="14px"
                  paddingY="4px"
                  borderRadius="full"
                  color="white"
                  backgroundColor={status ? STATUS_COLOR[status] : colors.text.mediumGray}
                  textTransform="capitalize"
                >
                  {status ?? 'unknown'}
                </Badge>
                <Text fontSize="14px" color="text.mediumBlue" fontWeight={700}>
                  {alerts.length === 0 ? 'No active alerts' : `${alerts.length} active alert${alerts.length === 1 ? '' : 's'}`}
                </Text>
                {SEVERITY_ORDER.filter((s) => (bySeverity[s] ?? 0) > 0).map((s) => (
                  <HStack key={s} gap="6px">
                    <Box width="8px" height="8px" borderRadius="full" backgroundColor={SEVERITY_COLOR[s]} />
                    <Text fontSize="12px" color="text.mediumGray" textTransform="capitalize">
                      {s}: {bySeverity[s]}
                    </Text>
                  </HStack>
                ))}
              </HStack>

              {alerts.length > 0 && (
                <VStack alignItems="stretch" gap="8px">
                  {alerts.map((alert, i) => (
                    <HStack
                      key={`${alert.metric}-${i}`}
                      justifyContent="space-between"
                      gap="12px"
                      padding="12px 16px"
                      borderRadius="12px"
                      backgroundColor="background.lightGray"
                      borderLeft="4px solid"
                      borderColor={SEVERITY_COLOR[alert.severity]}
                    >
                      <VStack alignItems="flex-start" gap="2px" minWidth={0}>
                        <Text fontSize="14px" fontWeight={700} color="text.darkBlue">
                          {alert.message}
                        </Text>
                        <Text fontSize="12px" color="text.mediumGray">
                          {alert.metric} · value {alert.value} (threshold {alert.threshold})
                        </Text>
                      </VStack>
                      <Badge
                        flexShrink={0}
                        color="white"
                        backgroundColor={SEVERITY_COLOR[alert.severity]}
                        borderRadius="full"
                        paddingX="10px"
                        textTransform="uppercase"
                        fontSize="10px"
                      >
                        {alert.severity}
                      </Badge>
                    </HStack>
                  ))}
                </VStack>
              )}
            </VStack>
          )}
        </SectionCard>

        {/* Identity metrics */}
        <SectionCard title="Identity" subtitle="How user lookups are served (our DB vs. Auth0) and sync reliability.">
          {identity.isLoading ? (
            <HStack justifyContent="center" paddingY="20px">
              <LogoSpinner width="40px" />
            </HStack>
          ) : identity.isError || !identity.data ? (
            <Text color="extra.red" fontSize="14px">
              Couldn’t load identity metrics.
            </Text>
          ) : (
            <VStack alignItems="stretch" gap="14px">
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="14px">
                {IDENTITY_METRICS.map((m) => (
                  <MetricTile key={m.key} label={m.label} value={identity.data?.[m.key] as number} hint={m.hint} bad={m.bad} />
                ))}
              </SimpleGrid>
              {identityHealth.data && !identityHealth.data.healthy && identityHealth.data.warnings.length > 0 && (
                <VStack
                  alignItems="flex-start"
                  gap="4px"
                  padding="12px 16px"
                  borderRadius="12px"
                  backgroundColor="rgba(204, 32, 32, 0.08)"
                >
                  <Text fontSize="13px" fontWeight={700} color="extra.darkRed">
                    Health warnings
                  </Text>
                  {identityHealth.data.warnings.map((w, i) => (
                    <Text key={i} fontSize="13px" color="text.darkBlue">
                      • {w}
                    </Text>
                  ))}
                </VStack>
              )}
            </VStack>
          )}
        </SectionCard>

        {/* Video pipeline metrics */}
        <SectionCard title="Video Pipeline" subtitle="Mux upload lifecycle, webhook integrity and asset-deletion reliability.">
          {video.isLoading ? (
            <HStack justifyContent="center" paddingY="20px">
              <LogoSpinner width="40px" />
            </HStack>
          ) : video.isError || !video.data ? (
            <Text color="extra.red" fontSize="14px">
              Couldn’t load video pipeline metrics.
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="14px">
              {VIDEO_METRICS.map((m) => (
                <MetricTile key={m.key} label={m.label} value={video.data?.[m.key] as number} hint={m.hint} bad={m.bad} />
              ))}
            </SimpleGrid>
          )}
        </SectionCard>
      </VStack>
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/super-admin/metrics')({
  component: MetricsPage,
});
